import { Property } from "csstype";
import { APP_ENV, CDN_URL, IMAGE_CDN_URL, STATIC_FILE_PATH } from "@shared/env";
import url from "@shared/utils/urls/url";
import cdn from "@shared/utils/urls/cdn";
import { ImageLoaderProps } from "next/image";

export function useImageLoader() {
  /**
   * Image loader for local environments
   *
   * this is actually not the right string,
   * current cdn url doesn't support fitering widths and quality
   * faked width and quality query params so server would stop complaining
   */
  const imageLoader = ({ src, width, quality }: ImageLoaderProps) => {
    let imagePath = src;

    // if full path is provided return it (e.g.: for local env paths with no CDN)
    if (imagePath.startsWith("https://")) {
      return imagePath;
    }

    // If the image already has CDN_URL or STATIC_FILE_PATH prefixed, avoid duplication
    if (imagePath.startsWith(STATIC_FILE_PATH)) {
      imagePath = imagePath.slice(STATIC_FILE_PATH.length);
    } else if (imagePath.startsWith(cdn())) {
      return `${imagePath}?width=${width}&q=${quality || 75}`;
    }

    return cdn([imagePath, `?width=${width}&q=${quality || 75}`]);
  };

  const stripDomain = (domain: string, path: string): string => {
    let strToRemove = domain;
    if (!strToRemove.endsWith("/")) {
      strToRemove += "/";
    }
    return path.replace(strToRemove, "");
  };

  /**
   * For development, if the image is not on the CDN, return the path as is
   * This is to allow for local and preview (Vercel) development without needing to upload images to the CDN
   */
  const cleanPathForDevelopment = (src: string): string => {
    let imagePath = src;
    // add leading slash if required
    if (
      !src.startsWith("/") &&
      !src.startsWith(IMAGE_CDN_URL) &&
      !src.startsWith(CDN_URL)
    ) {
      imagePath = `/${src}`;
    }

    return imagePath;
  };

  /**
   * Image loader using serverless resize url
   */
  const serverlessResizeImageLoader = ({
    src,
    width,
    quality,
    aspectRatio = "1",
    missingFromCDN,
  }: ImageLoaderProps & {
    aspectRatio: Property.AspectRatio | undefined;
    missingFromCDN: boolean;
  }): string => {
    let imagePath = src;

    // for use when adding new static image that hasn't been uploaded to the CDN yet
    // must create task to change missingFromCDN flag back to false once image is uploaded
    if (missingFromCDN && (APP_ENV === "local" || APP_ENV === "preview")) {
      imagePath = cleanPathForDevelopment(imagePath);
      return imagePath;
    }

    // If image starts with a CDN-like url, remove it to prepare for the serverless resize url generation
    if (src.startsWith(CDN_URL) && CDN_URL !== "/") {
      imagePath = stripDomain(CDN_URL, src);
    } else if (src.startsWith(IMAGE_CDN_URL) && IMAGE_CDN_URL !== "/") {
      return imagePath;
    }

    return url(
      [
        "fit-in",
        "{width}x{height}",
        "filters:quality({quality}):format(webp)",
        imagePath,
      ],
      {
        width,
        height: width / Number(aspectRatio),
        quality: quality || 100,
      },
      { origin: IMAGE_CDN_URL }
    );
  };

  return {
    serverlessResizeImageLoader,
    imageLoader,
  };
}
